import React, { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Dropdown } from "react-bootstrap";
import swal from "sweetalert";
import {
  cambiarConjunto,
  obtenerMisConjuntos,
  saveResidenteTokenInLocalStorage,
} from "../../../../services/ResidenteAuthService";
import { loginConfirmedAction } from "../../../../store/actions/AuthActions";
import { loginPermisosAction } from "../../../../store/actions/PermisosActions";

const ConjuntoSelector = () => {
  const dispatch = useDispatch();
  const auth = useSelector((state) => state.auth.auth);
  const user = auth?.user;

  const [conjuntos, setConjuntos] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isChanging, setIsChanging] = useState(false);
  const [conjuntoActual, setConjuntoActual] = useState(null);

  const esResidente = user?.tipo === "RESIDENTE";

  const cargarConjuntos = useCallback(async () => {
    if (!esResidente || !auth?.access) return;

    setIsLoading(true);
    try {
      const response = await obtenerMisConjuntos(auth.access);
      if (response.data.success && response.data.conjuntos) {
        setConjuntos(response.data.conjuntos);

        const perfilResidente = user?.perfil_residente;
        if (perfilResidente?.citofono_id) {
          const actual = response.data.conjuntos.find(
            (c) => c.id === perfilResidente.citofono_id,
          );
          setConjuntoActual(actual || response.data.conjuntos[0] || null);
        } else if (response.data.conjuntos.length > 0) {
          setConjuntoActual(response.data.conjuntos[0]);
        }
      }
    } catch (error) {
      console.error("Error al cargar conjuntos:", error);
    } finally {
      setIsLoading(false);
    }
  }, [esResidente, auth?.access, user?.perfil_residente]);

  useEffect(() => {
    cargarConjuntos();
  }, [cargarConjuntos]);

  const handleCambiarConjunto = async (conjunto) => {
    if (!conjunto || conjunto.id === conjuntoActual?.id) return;

    setIsChanging(true);
    try {
      const response = await cambiarConjunto(conjunto.id, auth.access);

      if (response.data.success) {
        const tokenData = saveResidenteTokenInLocalStorage(response.data);
        dispatch(loginConfirmedAction(tokenData));
        dispatch(loginPermisosAction());

        setConjuntoActual(conjunto);

        swal({
          title: "¡Listo!",
          text: `Cambiaste a ${conjunto.nombre_conjunto || "tu nuevo conjunto"}`,
          icon: "success",
          timer: 2000,
          buttons: false,
        });

        setTimeout(() => {
          window.location.reload();
        }, 1500);
      }
    } catch (error) {
      console.error("Error al cambiar conjunto:", error);
      swal(
        "Error",
        error.response?.data?.message || "No se pudo cambiar de conjunto",
        "error",
      );
    } finally {
      setIsChanging(false);
    }
  };

  if (!esResidente || conjuntos.length <= 1) {
    return null;
  }

  return (
    <div className="conjunto-selector-container px-3 pb-3">
      <Dropdown className="conjunto-dropdown">
        <Dropdown.Toggle
          variant="outline-primary"
          size="sm"
          className="w-100 d-flex align-items-center justify-content-between"
          disabled={isLoading || isChanging}
          id="conjunto-selector-dropdown"
        >
          {isLoading ? (
            <span>
              <i className="fa fa-spinner fa-spin me-2"></i>
              Cargando...
            </span>
          ) : isChanging ? (
            <span>
              <i className="fa fa-spinner fa-spin me-2"></i>
              Cambiando...
            </span>
          ) : (
            <span className="text-truncate">
              <i className="fa fa-building me-2"></i>
              {conjuntoActual?.nombre_conjunto || "Seleccionar"}
            </span>
          )}
        </Dropdown.Toggle>

        <Dropdown.Menu className="w-100 conjunto-dropdown-menu">
          <Dropdown.Header className="text-muted small">
            <i className="fa fa-exchange-alt me-1"></i>
            Cambiar de conjunto
          </Dropdown.Header>
          <Dropdown.Divider />

          {conjuntos.map((conjunto) => (
            <Dropdown.Item
              key={conjunto.id}
              onClick={() => handleCambiarConjunto(conjunto)}
              active={conjunto.id === conjuntoActual?.id}
              disabled={isChanging}
              className="conjunto-item"
            >
              <div className="d-flex align-items-center">
                <i
                  className={`fa fa-building me-2 ${
                    conjunto.id === conjuntoActual?.id
                      ? "text-primary"
                      : "text-muted"
                  }`}
                ></i>
                <div className="flex-grow-1 text-truncate">
                  <div className="fw-medium">
                    {conjunto.nombre_conjunto || "Sin nombre"}
                  </div>
                  {conjunto.direccion_unidad && (
                    <small className="text-muted d-block text-truncate">
                      {conjunto.direccion_unidad}
                    </small>
                  )}
                </div>
                {conjunto.id === conjuntoActual?.id && (
                  <i className="fa fa-check text-primary ms-2"></i>
                )}
              </div>
            </Dropdown.Item>
          ))}
        </Dropdown.Menu>
      </Dropdown>

      <style>{`
        .conjunto-selector-container {
          border-bottom: 1px solid rgba(0, 0, 0, 0.1);
          margin-bottom: 0.5rem;
        }
        .conjunto-dropdown .dropdown-toggle {
          font-size: 0.85rem;
          padding: 0.5rem 0.75rem;
          border-radius: 8px;
        }
        .conjunto-dropdown .dropdown-toggle::after {
          margin-left: auto;
        }
        .conjunto-dropdown-menu {
          max-height: 300px;
          overflow-y: auto;
          border-radius: 8px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        }
        .conjunto-item {
          padding: 0.6rem 1rem;
          border-radius: 4px;
          margin: 0 0.25rem;
        }
        .conjunto-item:hover {
          background-color: rgba(59, 130, 246, 0.1);
        }
        .conjunto-item.active {
          background-color: rgba(59, 130, 246, 0.15);
          color: inherit;
        }
        .conjunto-item .fw-medium {
          font-weight: 500;
        }
      `}</style>
    </div>
  );
};

export default ConjuntoSelector;
